import { z } from 'zod';
import { roleSchema } from './roles.js';

export const slugSchema = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits and single dashes only');

export const orgStatusSchema = z.enum(['active', 'suspended']);
export type OrgStatus = z.infer<typeof orgStatusSchema>;

export const organizationSchema = z.object({
  id: z.string(),
  /**
   * The short number in the organization's URL — `/organizations/23`.
   *
   * Optional because a row created before the backfill ran has none, and
   * because the id remains a valid address; the web builds its links as
   * `number ?? id` exactly as it does for an OLT.
   */
  number: z.number().int().nullable().optional(),
  name: z.string(),
  slug: slugSchema,
  status: orgStatusSchema,
  /**
   * Whether this platform bills this organization at all.
   *
   * Separate from `status`: a suspended org that owes nothing is still
   * suspended, and an unbilled org is still free to use the platform. Optional
   * so an older client that has never heard of it reads an org unchanged.
   */
  billingEnabled: z.boolean().optional(),
  /**
   * Whether VLAN 1 is offered as a subscriber VLAN anywhere in this tenant.
   *
   * Off by default, and off means HIDDEN FROM PICKERS — not refused. An ONU
   * already riding VLAN 1 keeps working with this off: its WAN is editable,
   * its config reads and applies, and every list and report shows its real
   * VLAN. See `vlanSelectableForOnus` in `olt.ts`, which is the rule itself.
   *
   * Optional so an older client that has never heard of it reads an org
   * unchanged.
   */
  vlan1Selectable: z.boolean().optional(),
  /** When the status last moved, and the reason typed with it. Null on an org nobody has suspended. */
  statusChangedAt: z.number().int().nullable().optional(),
  statusNote: z.string().nullable().optional(),
  /**
   * When the scheduled purge will delete this organization. Null = never.
   *
   * A value here means ARCHIVED, which is stricter than suspended: suspended is
   * a wall, archived is a wall with a date on it. Reversible until the day
   * comes — `POST /orgs/:id/migration/unarchive` clears this and lifts the
   * suspension together. Optional so an older client reads an org unchanged.
   */
  purgeAfter: z.number().int().nullable().optional(),
  createdAt: z.number().int(),
  deviceCount: z.number().int().optional(),
  siteCount: z.number().int().optional(),
  userCount: z.number().int().optional(),
});
export type Organization = z.infer<typeof organizationSchema>;

export const createOrgSchema = z.object({
  name: z.string().min(2).max(120),
  slug: slugSchema,
});
export type CreateOrgRequest = z.infer<typeof createOrgSchema>;

/**
 * A blank box means "clear it", an absent key means "leave it alone".
 *
 * The distinction is the whole reason these fields are `.nullable()` AND
 * accept `''`: a form posts every input it renders, so without the empty
 * string meaning null, clearing a contact would be impossible — the only way
 * to say "nothing" from a text box is to send nothing.
 */
const clearableText = (max: number) => z.string().trim().max(max).nullable().optional();

export const updateOrgSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  /**
   * Changing this MOVES the org's platform subdomain (<slug>.<base>) and
   * retags its devices at the shared GenieACS. Never a silent field — the UI
   * spells the consequence out before the save, and the API answers with what
   * actually moved.
   */
  slug: slugSchema.optional(),
  status: orgStatusSchema.optional(),
  /** Why the status moved. Recorded on the org, not only in the audit trail. */
  statusNote: z.string().max(400).nullable().optional(),
  /**
   * Turn billing for this organization off or back on.
   *
   * Off takes a bill away; it never forgives one already owed. Turning it back
   * on resumes from `paidThrough` exactly as before, because the months an org
   * was not billed for are not months it silently accrued.
   */
  billingEnabled: z.boolean().optional(),
  /**
   * Offer VLAN 1 as a subscriber VLAN across this organization.
   *
   * Turning it OFF hides VLAN 1 from the pickers; it never takes an ONU that
   * is already on VLAN 1 off the air, and never refuses an edit to one. That
   * distinction is the point of the setting — see `vlanSelectableForOnus`.
   */
  vlan1Selectable: z.boolean().optional(),
  /**
   * Who to ring at this tenant. Ordinary editing, and the reason this schema
   * grew: an organization page that can suspend a company but cannot record
   * its accounts clerk's number sends the operator to a spreadsheet.
   *
   * The email is checked as an address and the phone as an international
   * number (the API normalises it to E.164 before storing) so that what is
   * kept here is something somebody could actually use — a contact detail
   * nobody can send to is worse than an empty field, because it reads as an
   * answer.
   */
  contactName: clearableText(120),
  contactEmail: z
    .string()
    .trim()
    .max(200)
    // Blank is how a form says "clear it", so it cannot be rejected as a
    // malformed address — and a bare `.email()` would reject it.
    .refine(
      (v) => v === '' || z.string().email().safeParse(v).success,
      'Use an email address like billing@theirisp.com',
    )
    .nullable()
    .optional(),
  contactPhone: clearableText(40),
  /**
   * The platform's private note about this tenant. Superadmin only, and it
   * never reaches a tenant's DTO — see `organizations.platform_note` and
   * `ORG_OWN_FIELDS`. Not the suspension reason: that is `statusNote`, which
   * the tenant is shown on purpose.
   */
  platformNote: clearableText(2000),
});
export type UpdateOrgRequest = z.infer<typeof updateOrgSchema>;

/**
 * Fields no PATCH accepts, and where each is really changed.
 *
 * Named rather than silently dropped. `z.object()` strips what it does not
 * know, so a caller who sends `accountNumber` today gets a 200, a fresh
 * `updatedAt` and an unchanged account number — a save that reports success
 * and changed something other than what was asked. `PATCH /api/auth/me`
 * refuses `email` and `phone` the same way and for the same reason.
 *
 * Shared rather than private to the route because the Organizations page shows
 * these fields read-only and has to say WHY beside each one; two copies of the
 * sentence is how the page and the API end up disagreeing about the rule.
 */
export const ORG_REFUSED_FIELDS: Record<string, string> = {
  accountNumber:
    'The account number is issued once and never reissued: it is printed on every invoice, ' +
    'typed into phones as the M-PESA account reference, and sitting in payers’ payment ' +
    'history. Renumbering it orphans money already in flight — Daraja answers "The Account ' +
    'Number you provided is not exist" for an account no shortcode knows. Nothing was changed.',
  customDomain:
    'A white-label domain is not a text field: it is only stored once the CNAME has been ' +
    'verified pointing here AND an nginx vhost has been written, so a stored domain always ' +
    'means a live host. Change it under Settings → White-labeling (POST /api/branding/' +
    'domain/verify, then /apply). Nothing was changed.',
  purgeAfter:
    'A deletion date is set by archiving an organization (POST /api/orgs/:id/migration/apply ' +
    'with sourceDisposition "archive") and cleared by POST /api/orgs/:id/migration/unarchive, ' +
    'which lifts the suspension with it. Setting the date alone would leave an active ' +
    'organization counting down to deletion. Nothing was changed.',
};

/**
 * The ONLY fields an organization may change about ITSELF. Everything else on
 * `updateOrgSchema` is the platform's.
 *
 * An ALLOW-list, and that shape is the point. The first cut of this tier was a
 * deny-list of platform fields, which meant every field added to
 * `updateOrgSchema` afterwards became tenant-writable by default — and the
 * first one added was the platform's private note about the tenant. A
 * deny-list has to be remembered; an allow-list has to be opened. The failure
 * mode of forgetting is now a superadmin-only field, not a leak.
 *
 * The contact is the tenant's own fact: three lines saying who to ring at that
 * company. It appears on no document this platform has issued, so a tenant
 * correcting it changes nothing anybody else is already holding.
 *
 * `name` is deliberately NOT here, and that is the interesting exclusion,
 * because "an admin cannot fix a typo in their own company's name" is what
 * this tier was built for. The name is not the tenant's own text: it is what
 * the platform ASSERTS about them on documents it has already issued.
 * `services/billing/public-invoice.ts` reads `organizations.name` live at
 * render into the `Billed to` line of every issued invoice's public page and
 * PDF, and `share-delivery.ts` does the same for what it emails. A tenant that
 * can rename itself can therefore change what a bill its own customer is
 * already holding says it was billed to — the party BILLED rewriting the
 * issuer's document. An issuer may correct its own paperwork; the payee may
 * not. So a rename stays a superadmin act, and an org admin who needs one asks
 * for it. (Freezing the name onto each invoice at issue is the other half of
 * this and the better long-term answer — it also constrains the superadmin —
 * but it belongs in `services/billing`, not here, and doing both would leave
 * two rules for one question.)
 *
 * `slug` moves the platform subdomain and the ACS device tag (a tenant that
 * can choose its own tag can choose another tenant's prefix); `status` is the
 * wall, and a tenant that can lift its own suspension is not suspended;
 * `statusNote` is the platform's record of why the wall went up;
 * `billingEnabled` is whether this platform charges them at all;
 * `vlan1Selectable` decides what every subscriber VLAN picker in the tenant
 * offers across a live fleet; `platformNote` is not theirs to read, let alone
 * to clear.
 */
export const ORG_OWN_FIELDS = ['contactName', 'contactEmail', 'contactPhone'] as const;
export type OrgOwnField = (typeof ORG_OWN_FIELDS)[number];

/** One kind of dependent row, counted. Mirrors the API's `refuseIfAttached` shape. */
export const orgAttachmentSchema = z.object({
  label: z.string(),
  count: z.number().int(),
});
export type OrgAttachment = z.infer<typeof orgAttachmentSchema>;

/**
 * Everything the delete decision needs in one answer.
 *
 * Removing an organization erases a whole tenant — the chassis inventory
 * included — so the page that offers it has to be able to show what is
 * actually in there. `deleteBlockers` is what refuses the delete right now;
 * `erases` is everything that would go with it if it went ahead.
 */
export const orgDetailSchema = organizationSchema.extend({
  updatedAt: z.number().int(),
  /**
   * The number this organization's money is paid AGAINST.
   *
   * Read-only here and refused by PATCH (see `ORG_REFUSED_FIELDS`), but it has
   * to be READABLE somewhere an operator can find it: it is what a customer
   * types into their phone as the M-PESA account reference, so "what account
   * do we pay to?" is a support question with an answer that lived only in the
   * billing page's payment descriptor until this field existed.
   *
   * Null on an organization created before account numbers existed and never
   * back-filled — `db/migrate.ts` fills them at boot, so in practice only a
   * database mid-upgrade shows one.
   */
  accountNumber: z.number().int().nullable(),
  /** Who to ring at this tenant. All three null on an org nobody has filled in. */
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  /** E.164, as `normaliseMsisdn` stored it. */
  contactPhone: z.string().nullable(),
  /**
   * The platform's private note about this tenant — PRESENT ONLY for a caller
   * holding `org.manage`.
   *
   * Optional, and the key is genuinely ABSENT rather than null for everyone
   * else. Null would answer a question the tenant is not entitled to ask: it
   * says "there is no note", which means a present-but-null field also says
   * "there IS one" the moment it stops being null. `loadOrgDetail` takes the
   * reader as an argument for exactly this.
   */
  platformNote: z.string().nullable().optional(),
  oltCount: z.number().int(),
  onuCount: z.number().int(),
  acsDeviceCount: z.number().int(),
  apiKeyCount: z.number().int(),
  zoneCount: z.number().int(),
  splitterCount: z.number().int(),
  speedProfileCount: z.number().int(),
  tr069ProfileCount: z.number().int(),
  invoiceCount: z.number().int(),
  paymentCount: z.number().int(),
  /** Start of the first unpaid month; null means the org has never paid. */
  paidThrough: z.number().int().nullable(),
  /**
   * The first month this organization may be charged for — a UTC month start.
   *
   * Null until the org is first assessed under a non-zero rate, when it seeds
   * to that month so charging never reaches backwards by accident. A
   * superadmin moves it through `PUT /api/orgs/:id/billing-since`, including
   * BACKWARDS to bill history on purpose; clearing it lets the next assessment
   * seed it again.
   */
  billingSince: z.number().int().nullable(),
  /** `<slug>.<base>`, or null while no base domain is configured. */
  platformSubdomain: z.string().nullable(),
  baseDomain: z.string().nullable(),
  /** The org's own white-label hostname, once verified and vhosted. */
  customDomain: z.string().nullable(),
  /** What its ONUs authenticate to the shared ACS with. Does NOT follow a slug change. */
  informUsername: z.string().nullable(),
  /** The GenieACS tag that binds a device to this tenant. */
  acsOrgTag: z.string(),
  deleteBlockers: orgAttachmentSchema.array(),
  erases: orgAttachmentSchema.array(),
  /** True when nothing blocks a hard delete. Suspend is still the recommended path. */
  deletable: z.boolean(),
});
export type OrgDetail = z.infer<typeof orgDetailSchema>;

/** Typing the org's exact name is the confirmation; there is no plain delete button. */
export const deleteOrgSchema = z.object({
  confirmName: z.string().min(1).max(120),
});
export type DeleteOrgRequest = z.infer<typeof deleteOrgSchema>;

/** What a slug change actually moved, reported back so the UI can show a receipt. */
export const slugChangeResultSchema = z.object({
  from: z.string(),
  to: z.string(),
  subdomainFrom: z.string().nullable(),
  subdomainTo: z.string().nullable(),
  /** Devices carrying the old `org:<slug>` tag, being retagged in the background. */
  acsDevicesRetagging: z.number().int(),
  /** Unchanged on purpose — it is written into every provisioned ONU. */
  informUsername: z.string().nullable(),
});
export type SlugChangeResult = z.infer<typeof slugChangeResultSchema>;

export const userStatusSchema = z.enum(['active', 'disabled']);

export const appUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable().optional(),
  role: roleSchema,
  orgId: z.string().nullable(),
  orgName: z.string().nullable().optional(),
  status: userStatusSchema,
  lastLoginAt: z.number().int().nullable(),
  createdAt: z.number().int(),
});
export type AppUser = z.infer<typeof appUserSchema>;

export const createUserSchema = z.object({
  email: z.string().email().max(200),
  name: z.string().min(1).max(120),
  password: z.string().min(10).max(200),
  role: roleSchema,
  /** Optional; the route normalises it to E.164 (2026-09-17). Blank means none. */
  phone: z.string().trim().max(40).nullable().optional(),
  /** Ignored for org-scoped callers; superadmin uses it to place the user. */
  orgId: z.string().nullable().optional(),
});
export type CreateUserRequest = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  role: roleSchema.optional(),
  status: userStatusSchema.optional(),
  password: z.string().min(10).max(200).optional(),
  /**
   * The person's own to change, through the confirmation-code flow — except
   * for a platform administrator, who sets them outright (2026-09-17). The
   * route refuses both to any other caller by name, clears the proof stamp
   * on the value that changed, and switches off a two-step enrolment that
   * rode on that channel (saying so in ~BT~notes~BT~). Blank phone means none.
   */
  email: z.string().trim().email().max(200).optional(),
  phone: z.string().trim().max(40).nullable().optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserSchema>;

// --- Onboarding: an organization and its first administrator in one step ----
// (`POST /api/orgs/onboard`, 2026-09-17; operator: "lets create a wizard for
// creating a new organization and it's first admin".)

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

export const onboardOrgSchema = z.object({
  org: z.object({
    name: z.string().trim().min(2).max(120),
    slug: slugSchema,
    contactName: optionalText(120),
    contactEmail: optionalText(200),
    contactPhone: optionalText(40),
  }),
  admin: z.object({
    name: z.string().trim().min(1).max(120),
    /** Their username on the platform; stored lower-cased. */
    email: z.string().trim().email().max(200),
    phone: optionalText(40),
    password: z.string().min(10).max(200),
  }),
  welcome: z
    .object({
      /** Send the administrator their sign-in address and username. */
      email: z.boolean().default(false),
      /** …and the password with it. Off unless the operator says so: a mail body gets forwarded. */
      includePassword: z.boolean().default(false),
    })
    .default({ email: false, includePassword: false }),
  /** Answer the plan and write nothing — what the wizard's Review step asks. */
  dryRun: z.boolean().default(false),
});
export type OnboardOrgRequest = z.infer<typeof onboardOrgSchema>;

export const onboardIssueSchema = z.object({
  field: z.enum(['org.name', 'org.slug', 'org.contactEmail', 'org.contactPhone', 'admin.email', 'admin.phone', 'welcome']),
  message: z.string(),
});
export type OnboardIssue = z.infer<typeof onboardIssueSchema>;

/** What a create WOULD do: every value as it will be stored, and every reason it would be refused. */
export const onboardPlanSchema = z.object({
  org: z.object({
    name: z.string(),
    slug: z.string(),
    contactName: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    /** `<slug>.<base domain>` when the platform has a base domain; null otherwise. */
    subdomain: z.string().nullable(),
    /** Where the administrator is told to sign in. */
    signInUrl: z.string(),
  }),
  admin: z.object({ name: z.string(), email: z.string(), phone: z.string().nullable() }),
  welcome: z.object({
    requested: z.boolean(),
    includePassword: z.boolean(),
    /** Whether a welcome mail can go at all (SMTP configured). */
    canSend: z.boolean(),
    reason: z.string().nullable(),
  }),
  issues: z.array(onboardIssueSchema),
});
export type OnboardPlan = z.infer<typeof onboardPlanSchema>;

export const onboardResultSchema = z.object({
  dryRun: z.boolean(),
  plan: onboardPlanSchema,
  org: organizationSchema.nullable(),
  admin: appUserSchema.nullable(),
  /** Null when no welcome mail was asked for. `queued`, never "sent": the outbox worker talks to the relay. */
  welcome: z.object({ queued: z.boolean(), note: z.string() }).nullable(),
});
export type OnboardResult = z.infer<typeof onboardResultSchema>;

export const auditEntrySchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  actorId: z.string().nullable(),
  actorLabel: z.string(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  detail: z.record(z.unknown()).nullable(),
  ip: z.string().nullable(),
  at: z.number().int(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;
